
import arr from 'dojo/_base/array';
import lang from 'dojo/_base/lang';
import xhr from 'dojo/request/xhr';
import topic from 'dojo/topic';
import handlers from 'dojo/request/handlers';
import ParserWithBenefits from '../moreCore/Tools/_JsonParserWithBenefits';

interface dummyXhrType // weil die MOZ_EvBookingBase irgendwas anderes macht bei der call signature...
{
    /**
    * Sends a request using XMLHttpRequest with the given URL and options.
    */
    <T>(url: string, method:string, data:object|any): dojo.request.Promise<any>;
}

interface MonitoredRequest {
    requestname: string;
    id: number;
    complete: boolean;
    warningTimeoutHandle: number | null;
    preWarningTimeoutHandle: number | null;
    warningPublished?: boolean;
    preWarningPublished?: boolean;
}

interface CallfunctionOptions {
    skipXssCheck?: boolean;
}

interface ErrorDataObject {
    Message: string;
    Details: string;
    functionWhitelistError: boolean;
    [key: string]: any; // For additional error properties
}

interface CustomerSettingsModule {
    name: string;
    requestTimeoutWarning?: number;
    requestTimeoutPreWarning?: number;
}

class RequestManagerTS {

    // --- Properties ---

    private _runningRequests: any[] = [];
    private _monitoredRequests: MonitoredRequest[] = [];
    private _customerSettingsModule: CustomerSettingsModule | null = null;

    public silentExceptions: boolean = true;
    public handleAs: string = "jsonWithDateWarning";
    public timeoutWarningSeconds: number = 30;
    public timeoutPreWarningSeconds: number = 5;
    public additionalErrorProperties: string[] = [];

    public xhr: dojo.request.Xhr | dummyXhrType;

    // --- Lifecycle Methods ---

    constructor() {
        this.xhr = xhr;
        this._runningRequests = [];
        this._monitoredRequests = [];

        // Handler mit integrierter Datumskonvertierung
        handlers.register("jsonWithDate", (response: any) => {
            return ParserWithBenefits.parse(response.text);
        });

        handlers.register("jsonWithDateWarning", (response: any) => {
            return ParserWithBenefits.parseWithWarnings(response.text, response.url);
        });
    }

    public postCreate(): void {
        // In TypeScript, `this.inherited(arguments)` is often handled by a super call
        // but following the Dojo pattern, it might be called like this.
        (this as any).inherited(arguments);

        try {
            const customerSettings = (window as any).customerSettings;
            if (customerSettings?.modules?.length) {
                for (const module of customerSettings.modules) {
                    if (module.name === "SystemStateMonitor") {
                        this._customerSettingsModule = module;
                        if (typeof module.requestTimeoutWarning === "number") {
                            this.timeoutWarningSeconds = module.requestTimeoutWarning;
                        }
                        if (typeof module.requestTimeoutPreWarning === "number") {
                            this.timeoutPreWarningSeconds = module.requestTimeoutPreWarning;
                        }
                        break; // Exit loop once found
                    }
                }
            }
        } catch (ex) {
            console.error("Error processing customer settings in RequestManager:", ex);
        }
    }

    // --- Public Methods ---

    public callfunction(
        functionname: string,
        jsonData: any,
        responseCallback?: (response: any) => void,
        errorCallback?: (error: any) => void,
        commitIfSuccessful?: boolean,
        options?: CallfunctionOptions
    ): dojo.request.Promise<{}> {
        const resCallback = responseCallback || (() => { });
        const errCallback = errorCallback || (() => { });
        const sync = false;

        let callfunctionControllerAction = "Data/Callfunction";
        if (options?.skipXssCheck === true) {
            callfunctionControllerAction = "Data/Callfunction_NoXssCheck";
        }

        const requestData: { [key: string]: any } = {
            "functionname": functionname,
            "jsonData": typeof jsonData === "object" ? (ParserWithBenefits as any).stringify(jsonData) : jsonData
        };

        if (commitIfSuccessful === true) {
            requestData.bCommitIfSuccess = true;
        }

        let connectionId = "";
        try {
            connectionId = (window as any).signalR?.getConnectionId() || null;
        } catch (ex) {
            console.warn("Could not retrieve signalR connectionId", ex);
        }

        const monitorData = this._addRequestMonitor(functionname);

        let doRequest = () => {
            const request = xhr(
                (window as any).appRoot + callfunctionControllerAction + "?fct=" + functionname,
                {
                    handleAs: this.handleAs,
                    method: "POST",
                    data: requestData,
                    preventCache: true,
                    sync: sync,
                    // B6: CSRF token injected by _Layout (window.csrfToken); server validates
                    // it on Data/Callfunction[_NoXssCheck]. A cross-site callfunction lacking
                    // this header is rejected. (The one added frontend request header.)
                    headers: { "ConnectionId": connectionId, "X-CSRF-TOKEN": (window as any).csrfToken }
                }
            ).then(
                lang.hitch(this, (monitorIndex: number, response: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    const errorObj = this._checkForException(functionname, response);
                    if (errorObj) {
                        if (errorObj.functionWhitelistError === true && !this.silentExceptions) {
                            const toastConfig = {
                                title: errorObj.Message,
                                message: "Check your webtool configuration!\r\n" + errorObj.Message,
                                iconClass: "fas fa-shield-alt w3-text-red",
                                options: { timeout: 30000 }
                            };
                            topic.publish("/Toaster/ShowToast", toastConfig);
                        }
                        errCallback(errorObj);
                    } else {
                        resCallback(response);
                    }
                }, monitorData.id) as any,
                lang.hitch(this, (monitorIndex: number, error: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    if (error?.name === "CancelError") {
                        return; // Request was cancelled, not a true error.
                    }
                    errCallback(error);
                    this._onError(error);
                }, monitorData.id) as any
            );
            this._managedRequestStack(request);
            return request;
        };

        //before return callFn() as any; - everything worked
        return doRequest() as any;
    }

    public post(
        url: string,
        parameters: any,
        responseCallback?: (response: any) => void,
        errorCallback?: (error: any) => void,
        sync: boolean = false
    ): dojo.request.Promise<{}> {
        const resCallback = responseCallback || (() => { });
        const errCallback = errorCallback || (() => { });

        let connectionId = "";
        try {
            connectionId = (window as any).signalR?.getConnectionId() || null;
        } catch (ex) {
            console.warn("Could not retrieve signalR connectionId", ex);
        }

        const monitorData = this._addRequestMonitor(url);

        let doRequest = () => {
            const request = xhr(url, {
                handleAs: this.handleAs,
                method: "POST",
                data: parameters,
                preventCache: true,
                sync: sync,
                headers: { "ConnectionId": connectionId }
            }).then(
                lang.hitch(this, (monitorIndex: number, response: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    const errorObj = this._checkForException(url, response);
                    if (errorObj) {
                        errCallback(errorObj);
                    } else {
                        resCallback(response);
                    }
                }, monitorData.id) as any,
                lang.hitch(this, (monitorIndex: number, error: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    if (error?.name === "CancelError") {
                        return;
                    }
                    errCallback(error);
                    this._onError(error);
                }, monitorData.id) as any
            );

            this._managedRequestStack(request);
            return request;
        };


        return doRequest() as any;
    }

    public get(
        url: string,
        responseCallback?: (response: any) => void,
        errorCallback?: (error: any) => void,
        sync: boolean = false
    ): dojo.request.Promise<{}> {
        const resCallback = responseCallback || (() => { });
        const errCallback = errorCallback || (() => { });

        const monitorData = this._addRequestMonitor(url);

        let doRequest = () => {
            const request = xhr(url, {
                handleAs: this.handleAs,
                method: "GET",
                preventCache: true,
                sync: sync
            }).then(
                lang.hitch(this, (monitorIndex: number, response: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    const errorObj = this._checkForException(url, response);
                    if (errorObj) {
                        errCallback(errorObj);
                    } else {
                        resCallback(response);
                    }
                }, monitorData.id) as any,
                lang.hitch(this, (monitorIndex: number, error: any) => {
                    this._completeMonitoredCallfunction(monitorIndex);
                    if (error?.name === "CancelError") {
                        return;
                    }
                    errCallback(error);
                    this._onError(error);
                }, monitorData.id) as any
            );

            this._managedRequestStack(request);
            return request;
        };

        return doRequest() as any;
    }

    public cancelRunningRequests(): void {
        //requests canceln
        for (const request of this._runningRequests) {
            if (request && !request.isResolved()) {
                if (typeof request.cancel == 'function'){
                    request.cancel();
                }else if(typeof request.abort == 'function'){
                    request.abort();
                }
            }
        }
        this._runningRequests = [];
    }

    public destroy(): void {
        this.cancelRunningRequests();
    }


    // --- Private Methods ---

    private _findMonitoredRequest(id: number): MonitoredRequest | undefined {
        return this._monitoredRequests.find(data => data.id === id);
    }

    private _completeMonitoredCallfunction(index: number): void {
        const data = this._findMonitoredRequest(index);
        if (data) {
            data.complete = true;
            if (data.warningTimeoutHandle) clearTimeout(data.warningTimeoutHandle);
            if (data.preWarningTimeoutHandle) clearTimeout(data.preWarningTimeoutHandle);
            data.warningTimeoutHandle = null;
            data.preWarningTimeoutHandle = null;

            if (data.warningPublished) {
                topic.publish("/Requestmanager/DurationWarning", data);
            }
            if (data.preWarningPublished) {
                topic.publish("/Requestmanager/DurationWarning/Pre", data);
            }
        }
    }

    private _onTimeoutPreWarningCallfunction(index: number): void {
        if (this.silentExceptions) return;

        const data = this._findMonitoredRequest(index);
        if (data && !data.complete) {
            data.preWarningPublished = true;
            topic.publish("/Requestmanager/DurationWarning/Pre", data);
        }
    }

    private _onTimeoutWarningCallfunction(index: number): void {
        if (this.silentExceptions) return;

        const data = this._findMonitoredRequest(index);
        if (data && !data.complete) {
            data.warningPublished = true;
            topic.publish("/Requestmanager/DurationWarning", data);
        }
    }

    private _addRequestMonitor(nameOrUrl: string): MonitoredRequest {
        const monitorId = this._monitoredRequests.length;
        const monitorData: MonitoredRequest = {
            requestname: nameOrUrl,
            id: monitorId,
            complete: false,
            warningTimeoutHandle: window.setTimeout(
                () => this._onTimeoutWarningCallfunction(monitorId),
                this.timeoutWarningSeconds * 1000
            ),
            preWarningTimeoutHandle: window.setTimeout(
                () => this._onTimeoutPreWarningCallfunction(monitorId),
                this.timeoutPreWarningSeconds * 1000
            )
        };
        this._monitoredRequests.push(monitorData);
        return monitorData;
    }

    private _managedRequestStack(request: any): void {
        if (arr.indexOf(this._runningRequests, request) === -1) {
            this._runningRequests.push(request);
        }
    }

    private _checkForException(url: string, response: any): ErrorDataObject | null {
        if (response && typeof response === "object") {
            if (response.Exception === true && response.ClientCallback !== true) {
                const msg = response.ErrorMessage || response.message || response.Message || "An unknown error occurred.";

                if (!this.silentExceptions) {
                    alert(`Fehler bei '${url.substr(0, 150)}': ${msg}`);
                }

                const errorData: ErrorDataObject = {
                    Message: msg,
                    Details: `Fehler bei '${url.substr(0, 150)}': ${msg}`,
                    functionWhitelistError: response.functionWhitelistError === true
                };

                if (this.additionalErrorProperties?.length) {
                    for (const prop of this.additionalErrorProperties) {
                        if (response.hasOwnProperty(prop)) {
                            errorData[prop] = response[prop];
                        }
                    }
                }
                return errorData;
            }
        }
        return null;
    }

    // --- Hooks for overriding ---
    protected _handleResponse(response: any): void {
        // Hook here
    }

    protected _onError(error: any): void {
        // Hook here
    }
}

export default RequestManagerTS;
